import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import { useDashboard } from "@/api/queries";
import type { Readiness } from "@/api/types";
import { formatBytes, pluralize } from "@/lib/format";

/**
 * The bottom status bar (desktop).
 *
 * Says what is actually true right now: whether the database and object storage
 * answer, and how much is really stored. Every figure comes from the API — there
 * is no invented quota, because HD-Vault has no quota.
 */
export function StatusBar() {
  const { data: dashboard } = useDashboard();
  const { data: health } = useQuery({
    queryKey: ["readiness"],
    queryFn: () => apiFetch<Readiness>("/health/ready"),
    // Slow poll: this is a reassurance indicator, not a monitoring system.
    refetchInterval: 60_000,
    retry: false,
  });

  const databaseUp = health?.database === "up";
  const storageUp = health?.storage === "up";

  return (
    <footer className="hidden shrink-0 items-center gap-4 border-t border-border bg-surface px-5 py-1.5 text-[11px] text-ink-subtle md:flex">
      <Indicator up={databaseUp} known={Boolean(health)} label="Base de données" />
      <Indicator up={storageUp} known={Boolean(health)} label="Stockage photos" />

      <span className="ml-auto flex min-w-0 items-center gap-4">
        {dashboard && (
          <>
            <span className="tnum hidden desk:inline">
              {pluralize(dashboard.product_count, "produit")} ·{" "}
              {pluralize(dashboard.location_count, "emplacement")} ·{" "}
              {pluralize(dashboard.container_count, "contenant")}
            </span>
            <span className="tnum hidden md:inline">
              {pluralize(dashboard.media_count, "fichier")} ·{" "}
              {formatBytes(dashboard.media_bytes_total)}
            </span>
          </>
        )}
        <span className="hidden whitespace-nowrap font-mono md:inline">HD-Vault {health?.version ?? "…"}</span>
      </span>
    </footer>
  );
}

function Indicator({ up, known, label }: { up: boolean; known: boolean; label: string }) {
  const tone = !known ? "bg-ink-subtle" : up ? "bg-status-available" : "bg-status-out";
  const text = !known ? "vérification…" : up ? "connecté" : "injoignable";
  return (
    <span
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap"
      title={`${label} : ${text}`}
    >
      <span className={`size-1.5 rounded-full ${tone}`} aria-hidden />
      <span>
        {label} <span className="text-ink-muted">{text}</span>
      </span>
    </span>
  );
}
